import React, { useState, useEffect } from 'react';

function Login({ onLogin }) {
  // step: 'password' | 'otp-request' | 'otp-verify'
  const [step, setStep] = useState('password');

  // Password login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // OTP login state
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [otpMobile, setOtpMobile] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpChallengeId, setOtpChallengeId] = useState('');
  const [otpMaskedMobile, setOtpMaskedMobile] = useState('');
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Probe whether OTP login is globally enabled so we know to render the button.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/auth/login-otp/enabled');
        const data = await res.json().catch(() => ({ enabled: false }));
        if (!cancelled) setOtpEnabled(Boolean(data?.enabled));
      } catch (_) {
        if (!cancelled) setOtpEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Countdown tick for OTP expiry
  useEffect(() => {
    if (step !== 'otp-verify' || otpSecondsLeft <= 0) return;
    const t = setInterval(() => setOtpSecondsLeft(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [step, otpSecondsLeft]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (res.ok) {
        onLogin(data.token, data.role, data.permissions || {}, data.username || username, data.refresh_token || '');
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpRequest = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login-otp/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile_number: otpMobile })
      });
      const data = await res.json();
      if (res.ok) {
        setOtpChallengeId(data.challenge_id);
        setOtpMaskedMobile(data.masked_mobile || '');
        setOtpSecondsLeft(Number(data.expires_in_seconds || 300));
        setOtpCode('');
        setStep('otp-verify');
      } else {
        setError(data.error || 'Could not send OTP');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login-otp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: otpChallengeId, otp: otpCode })
      });
      const data = await res.json();
      if (res.ok) {
        onLogin(data.token, data.role, data.permissions || {}, data.username || '', data.refresh_token || '');
      } else {
        setError(data.error || 'OTP verification failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const goBackToPassword = () => {
    setError('');
    setOtpCode('');
    setOtpChallengeId('');
    setOtpMaskedMobile('');
    setOtpSecondsLeft(0);
    setStep('password');
  };

  const formatSeconds = (s) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  return (
    <div className="flex min-h-screen" style={{ position: 'relative' }}>

      {/* ── Support contact — always visible, fixed top-right ── */}
      <div
        style={{
          position: 'fixed', top: '1.1rem', right: '1.1rem', zIndex: 50, width: '234px',
          background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0',
          boxShadow: '0 6px 20px rgba(15,23,42,0.12)', padding: '0.85rem 1rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
          <div style={{
            width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
            background: 'var(--theme-primary-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: '0.66rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
              For Support
            </div>
            <a
              href="tel:+914461556522"
              style={{ fontSize: '0.92rem', fontWeight: '700', color: 'var(--theme-primary-dark)', textDecoration: 'none' }}
            >
              +91 (0) 44 6155 6522
            </a>
          </div>
        </div>

        <div style={{ height: 1, background: '#e2e8f0', margin: '0 0 0.6rem' }} />

        <div style={{ fontSize: '0.66rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '0.35rem' }}>
          Business Hours
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.76rem', color: 'var(--theme-text-secondary)', marginBottom: '0.15rem' }}>
          <span>Monday – Friday</span>
          <span style={{ fontWeight: '600', color: '#334155' }}>9 AM – 6:30 PM</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.76rem', color: 'var(--theme-text-secondary)' }}>
          <span>Saturday</span>
          <span style={{ fontWeight: '600', color: '#334155' }}>10 AM – 6:30 PM</span>
        </div>
      </div>

      {/* ── Left branding panel (hidden on small screens) ── */}
      <div
        className="hidden md:flex flex-col items-center justify-center"
        style={{
          flex: '0 0 42%',
          background: 'linear-gradient(150deg, #1a2a5e 0%, var(--theme-primary-dark) 55%, #3c5a9a 100%)',
          padding: '3rem 2.5rem',
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: '-90px', right: '-90px', width: '280px', height: '280px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <div style={{ position: 'absolute', bottom: '-70px', left: '-70px', width: '220px', height: '220px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ position: 'absolute', top: '40%', right: '-40px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

        <div style={{ marginBottom: '1.5rem', background: '#fff', borderRadius: '12px', padding: '10px 20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src="/easeroom-logo.png" alt="EaseRoom" style={{ height: '36px', display: 'block' }} />
        </div>

        <h1 style={{ fontSize: '1.9rem', fontWeight: '700', margin: '0 0 0.4rem', textAlign: 'center', letterSpacing: '-0.3px', color: '#ffffff', textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
          EASEPMS
        </h1>
        <p style={{ fontSize: '0.85rem', opacity: 0.7, textAlign: 'center', margin: '0 0 2.5rem', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
          Hospitality Experience Management Platform
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', width: '100%', maxWidth: '230px' }}>
          {[
            { icon: '🏨', text: 'Reservations & Check-in' },
            { icon: '🍽️', text: 'KOT / POS / Banquet' },
            { icon: '📋', text: 'Housekeeping & Rooms' },
            { icon: '📊', text: 'Reports & Compliance' },
          ].map(item => (
            <div key={item.text} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', background: 'rgba(255,255,255,0.09)', borderRadius: '8px', padding: '0.55rem 0.9rem', fontSize: '0.83rem' }}>
              <span>{item.icon}</span>
              <span style={{ opacity: 0.9 }}>{item.text}</span>
            </div>
          ))}
        </div>

        <p style={{ position: 'absolute', bottom: '1.5rem', fontSize: '0.72rem', opacity: 0.45, letterSpacing: '0.3px' }}>
          &#169; 2026 EASEROOM. All rights reserved.
        </p>
      </div>

      {/* ── Right login panel ── */}
      <div className="flex-1 flex items-center justify-center bg-slate-50 p-6">
        <div style={{ width: '100%', maxWidth: '400px' }}>

          <div className="md:hidden text-center mb-8">
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '52px', height: '52px', borderRadius: '14px', background: 'var(--theme-primary-dark)', marginBottom: '0.75rem' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 22V8l9-6 9 6v14H3z" />
                <rect x="9" y="13" width="6" height="9" />
              </svg>
            </div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--theme-primary-dark)', margin: 0 }}>EASEPMS</h1>
          </div>

          <div className="card">
            <div className="card-body" style={{ padding: '2.25rem' }}>

              {step === 'password' && (
                <>
                  <div style={{ marginBottom: '1.75rem' }}>
                    <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.4rem', fontWeight: '700', color: 'var(--theme-primary-dark)' }}>
                      Welcome back
                    </h2>
                    <p style={{ margin: 0, color: 'var(--theme-text-secondary)', fontSize: '0.875rem' }}>
                      Sign in to your account to continue
                    </p>
                  </div>

                  <form onSubmit={handlePasswordSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        Username
                      </label>
                      <input
                        placeholder="Username or mobile number"
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                        required
                        autoComplete="username"
                        style={{ width: '100%', padding: '0.7rem 0.9rem', fontSize: '0.95rem' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        Password
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          placeholder="Enter your password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          required
                          autoComplete="current-password"
                          style={{ width: '100%', padding: '0.7rem 2.8rem 0.7rem 0.9rem', fontSize: '0.95rem' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(v => !v)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          style={{ position: 'absolute', right: '0.7rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '3px', color: 'var(--theme-text-muted)', display: 'flex', alignItems: 'center' }}
                        >
                          {showPassword ? (
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.8 21.8 0 0 1 5.06-5.94" />
                              <path d="M9.9 4.24A10.9 10.9 0 0 1 12 4c7 0 11 8 11 8a22.07 22.07 0 0 1-3.17 4.36" />
                              <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                              <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                          ) : (
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    {error && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.6rem 0.9rem', color: 'var(--theme-danger)', fontSize: '0.85rem' }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary"
                      style={{ padding: '0.8rem', fontSize: '0.95rem', fontWeight: '600', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%', marginTop: '0.25rem' }}
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                          </svg>
                          Signing in…
                        </>
                      ) : (
                        <>
                          Sign In
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </>
                      )}
                    </button>
                  </form>

                  {otpEnabled && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '1.25rem 0 1rem' }}>
                        <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px' }}>or</span>
                        <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                      </div>
                      <button
                        type="button"
                        onClick={() => { setError(''); setStep('otp-request'); }}
                        style={{
                          width: '100%', padding: '0.75rem', fontSize: '0.92rem', fontWeight: '600',
                          borderRadius: '8px', border: '1px solid var(--theme-primary-dark)',
                          background: '#fff', color: 'var(--theme-primary-dark)', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="5" y="2" width="14" height="20" rx="2" />
                          <line x1="12" y1="18" x2="12.01" y2="18" />
                        </svg>
                        Login with OTP
                      </button>
                    </>
                  )}
                </>
              )}

              {step === 'otp-request' && (
                <>
                  <div style={{ marginBottom: '1.75rem' }}>
                    <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.4rem', fontWeight: '700', color: 'var(--theme-primary-dark)' }}>
                      Login with OTP
                    </h2>
                    <p style={{ margin: 0, color: 'var(--theme-text-secondary)', fontSize: '0.875rem' }}>
                      Enter the mobile number registered with your account.
                    </p>
                  </div>

                  <form onSubmit={handleOtpRequest} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        Mobile Number
                      </label>
                      <input
                        placeholder="10-digit mobile number"
                        value={otpMobile}
                        onChange={e => setOtpMobile(e.target.value.replace(/\D/g, '').slice(0, 12))}
                        inputMode="numeric"
                        required
                        autoFocus
                        style={{ width: '100%', padding: '0.7rem 0.9rem', fontSize: '0.95rem', letterSpacing: '1px' }}
                      />
                    </div>

                    {error && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.6rem 0.9rem', color: 'var(--theme-danger)', fontSize: '0.85rem' }}>
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading || otpMobile.length < 10}
                      className="btn-primary"
                      style={{ padding: '0.8rem', fontSize: '0.95rem', fontWeight: '600', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, width: '100%' }}
                    >
                      {loading ? 'Sending OTP…' : 'Send OTP'}
                    </button>

                    <button
                      type="button"
                      onClick={goBackToPassword}
                      style={{ width: '100%', padding: '0.6rem', fontSize: '0.85rem', fontWeight: '500', border: 'none', background: 'none', color: 'var(--theme-text-secondary)', cursor: 'pointer' }}
                    >
                      ← Back to password login
                    </button>
                  </form>
                </>
              )}

              {step === 'otp-verify' && (
                <>
                  <div style={{ marginBottom: '1.75rem' }}>
                    <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.4rem', fontWeight: '700', color: 'var(--theme-primary-dark)' }}>
                      Enter OTP
                    </h2>
                    <p style={{ margin: 0, color: 'var(--theme-text-secondary)', fontSize: '0.875rem' }}>
                      A 6-digit OTP has been sent to <strong>+91 {otpMaskedMobile}</strong>.
                    </p>
                  </div>

                  <form onSubmit={handleOtpVerify} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        OTP Code
                      </label>
                      <input
                        placeholder="6-digit OTP"
                        value={otpCode}
                        onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputMode="numeric"
                        maxLength={6}
                        required
                        autoFocus
                        style={{ width: '100%', padding: '0.7rem 0.9rem', fontSize: '1.2rem', letterSpacing: '8px', textAlign: 'center', fontWeight: '600' }}
                      />
                      {otpSecondsLeft > 0 ? (
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.78rem', color: 'var(--theme-text-muted)' }}>
                          OTP expires in {formatSeconds(otpSecondsLeft)}
                        </p>
                      ) : (
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.78rem', color: 'var(--theme-danger)' }}>
                          OTP expired — please resend.
                        </p>
                      )}
                    </div>

                    {error && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.6rem 0.9rem', color: 'var(--theme-danger)', fontSize: '0.85rem' }}>
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading || otpCode.length !== 6 || otpSecondsLeft <= 0}
                      className="btn-primary"
                      style={{ padding: '0.8rem', fontSize: '0.95rem', fontWeight: '600', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: (loading || otpSecondsLeft <= 0) ? 0.7 : 1, width: '100%' }}
                    >
                      {loading ? 'Verifying…' : 'Verify & Sign In'}
                    </button>

                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={goBackToPassword}
                        style={{ flex: 1, padding: '0.6rem', fontSize: '0.85rem', fontWeight: '500', border: 'none', background: 'none', color: 'var(--theme-text-secondary)', cursor: 'pointer', textAlign: 'left' }}
                      >
                        ← Back
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); setStep('otp-request'); }}
                        style={{ flex: 1, padding: '0.6rem', fontSize: '0.85rem', fontWeight: '500', border: 'none', background: 'none', color: 'var(--theme-primary-dark)', cursor: 'pointer', textAlign: 'right' }}
                      >
                        Resend OTP
                      </button>
                    </div>
                  </form>
                </>
              )}

            </div>
          </div>

          <p style={{ textAlign: 'center', marginTop: '1.25rem', color: 'var(--theme-text-muted)', fontSize: '0.75rem' }}>
            © 2026 EASEPMS · All rights reserved
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
